'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { get } from 'api';
import { useAuth } from './auth-provider/AuthJwtProvider';

// shape of the data we expect
const initialState = {
  roles: [],
  perms: [],
  menus: [],
  routes: [],
  loading: true,
  error: null,
};

const PermissionContext = createContext(initialState);

export const PermissionProvider = ({ children }) => {
  const [roles, setRoles] = useState(initialState.roles);
  const [perms, setPerms] = useState(initialState.perms);
  const [menus, setMenus] = useState(initialState.menus);
  const [routes, setRoutes] = useState(initialState.routes);
  const [loading, setLoading] = useState(initialState.loading);
  const [error, setError] = useState(initialState.error);
  const { sessionUser, setSessionUser } = useAuth();

  useEffect(() => {
    let isMounted = true;

    async function fetchPermissions() {
      try {
        setLoading(true);

        const res = await get('/auth/me/permissions');

        if (res?.status !== 200) {
          setSessionUser(null);
        }

        const data = res.data;
        if (isMounted) {
          setRoles(data.roles || []);
          setPerms(data.perms || []);
          setMenus(data.menus || []);
          setRoutes(data.routes || []);
          setError(null);
        }
      } catch (err) {
        console.error('PermissionContext fetch error:', err);
        if (isMounted) {
          setError(err.message || 'Unknown error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchPermissions();

    return () => {
      isMounted = false;
    };
  }, [sessionUser]);

  // nice helpers so consumers don't have to repeat logic

  // check if user has a specific permission
  const hasPerm = useMemo(() => {
    return (permKey) => perms.includes(permKey);
  }, [perms]);

  // check if user has any of these perms
  const hasAnyPerm = useMemo(() => {
    return (permKeys = []) => permKeys.some((p) => perms.includes(p));
  }, [perms]);

  // check if user can access a route path
  const canAccessRoute = useMemo(() => {
    return (path) => routes.includes(path);
  }, [routes]);

  const value = useMemo(
    () => ({
      roles,
      perms,
      menus,
      routes,
      loading,
      error,
      hasPerm,
      hasAnyPerm,
      canAccessRoute,
      // optional: setters if you want to update dynamically after login refresh etc.
      setRoles,
      setPerms,
      setMenus,
      setRoutes,
    }),
    [roles, perms, menus, routes, loading, error, hasPerm, hasAnyPerm, canAccessRoute],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

// tiny hook so components can consume easily
export function usePermission() {
  return useContext(PermissionContext);
}
